import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, Pencil, Trash2, MoreHorizontal, Sparkles } from 'lucide-react'

import { TableCell, TableRow } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { PolicyDraftListItem, PolicyDraftStatus } from '@/types/policy'

/**
 * Get the badge variant for a policy status
 */
function getStatusBadgeVariant(
  status: PolicyDraftStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'DRAFT':
      return 'secondary'
    case 'ACTIVE':
      return 'default'
    case 'INACTIVE':
      return 'outline'
    default:
      return 'secondary'
  }
}

/**
 * Get the display label for a policy status
 */
function getStatusLabel(status: PolicyDraftStatus): string {
  switch (status) {
    case 'DRAFT':
      return 'Draft'
    case 'ACTIVE':
      return 'Active'
    case 'INACTIVE':
      return 'Inactive'
    default:
      return status
  }
}

/**
 * Format a date string to a readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Check if a policy was auto-generated from a scan
 */
function isAutoGenerated(description: string | null): boolean {
  if (!description) return false
  return description.startsWith('Auto-generated from scan')
}

interface PolicyRowProps {
  /** Policy data */
  policy: PolicyDraftListItem
  /** Whether this row is selected */
  isSelected: boolean
  /** Callback to toggle selection */
  onToggleSelect: (id: string) => void
  /** Callback when view action is clicked */
  onView: (policy: PolicyDraftListItem) => void
  /** Callback when edit action is clicked */
  onEdit: (policy: PolicyDraftListItem) => void
  /** Callback when delete action is clicked */
  onDelete: (policy: PolicyDraftListItem) => void
}

/**
 * A single row in the policies table
 * Column order: Checkbox | Name | Status | Created By | Created | Updated | Actions
 */
export const PolicyRow = memo(function PolicyRow({
  policy,
  isSelected,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
}: PolicyRowProps) {
  const navigate = useNavigate()
  const isActive = policy.status === 'ACTIVE'
  const hasConversation = !!policy.conversationId

  return (
    <TableRow data-state={isSelected ? 'selected' : undefined}>
      <TableCell className="w-[40px]">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(policy.id)}
          aria-label={`Select ${policy.name}`}
        />
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {policy.name}
          {isAutoGenerated(policy.description) && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              Auto
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={getStatusBadgeVariant(policy.status)}>
          {getStatusLabel(policy.status)}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {policy.createdByUser?.email || 'Unknown'}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDate(policy.createdAt)}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDate(policy.updatedAt)}
      </TableCell>
      <TableCell className="w-[100px]">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onView(policy)}
            title="View policy"
          >
            <Eye className="h-4 w-4" />
            <span className="sr-only">View</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onEdit(policy)}
            disabled={isActive}
            title={isActive ? 'Undeploy first to edit' : 'Edit policy'}
          >
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(`/polix-ai/${policy.conversationId || policy.id}`)}
            disabled={isActive || !hasConversation}
            title={
              isActive
                ? 'Undeploy first to edit'
                : !hasConversation
                  ? 'No AI conversation available'
                  : 'Edit with AI'
            }
          >
            <Sparkles className="h-4 w-4" />
            <span className="sr-only">Edit with AI</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="More actions"
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">More actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(policy)}>
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onEdit(policy)}
                disabled={isActive}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(`/polix-ai/${policy.conversationId || policy.id}`)}
                disabled={isActive || !hasConversation}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Edit with AI
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(policy)}
                disabled={isActive}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  )
})
